import {
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import styles from './TaskPopover.module.css';

/**
 * Атрибуты для кнопки-триггера: связывают её с панелью в дереве доступности и
 * дают клавиатурный вход в список (ArrowDown, как в нативных комбобоксах).
 *
 * Третьим аргументом `trigger`, а не пропом на самом `TaskPopover`, потому что
 * триггер рисует вызывающий: только он знает, на какой элемент их вешать.
 * Аргумент необязательный — два десятка существующих вызовов его игнорируют.
 */
export type TaskPopoverTriggerProps = {
  'aria-haspopup': 'listbox' | 'menu' | 'dialog' | 'true';
  'aria-expanded': boolean;
  'aria-controls': string | undefined;
  onKeyDown: (e: ReactKeyboardEvent) => void;
};

export type TaskPopoverProps = {
  /** Триггер — кнопка/чип. Клик открывает панель. */
  trigger: (open: () => void, isOpen: boolean, triggerProps: TaskPopoverTriggerProps) => ReactNode;
  /**
   * Содержимое панели. `close` закрывает после выбора; `ready` = панель измерена
   * и видима — до этого кадра фокусировать в ней нечего (см. F22 ниже).
   */
  children: (close: () => void, ready: boolean) => ReactNode;
  /** Роль содержимого для триггера: listbox — список опций, menu — меню команд. */
  haspopup?: 'listbox' | 'menu' | 'dialog' | 'true';
  /** Имя панели для скринридера (`aria-label` на `role="dialog"`). */
  panelLabel?: string;
  /** Предпочтительное выравнивание панели относительно триггера (не жёсткое — collision может перекрыть). */
  align?: 'start' | 'end';
  /** Ширина панели (px). */
  width?: number;
  /**
   * Плотность панели. `menu` (дефолт) — паддинг под full-bleed строки списка, у
   * которых своя внутренняя отбивка. `content` — под прозу и кнопки: у них
   * собственного паддинга нет, и на меню-плотности текст жмётся к краям, а
   * рамка кнопки идёт в восьми пикселях параллельно рамке панели (гейт F12,
   * раунд 5). Собственный паддинг ребёнку не помогает — он складывается с
   * панельным в шаг, которого в шкале нет.
   */
  density?: 'menu' | 'content';
  /**
   * Предел высоты панели. Дефолт 360px рассчитан на СПИСКИ (исполнители, метки),
   * где прокрутка уместна. Словарь обозначений — не список: у него пять коротких
   * абзацев, и прокрутка прячет ровно ту строку, ради которой словарь и
   * открывают, — исключение из правила (BLOCKER дизайн-гейта D4, раунд 8:
   * содержимое 513px в панели 360px, пятая строка не видна).
   */
  maxHeight?: number;
  /**
   * Z-F9 — горизонтальный сдвиг панели от якоря (px, до clamp'а в viewport).
   *
   * Нужен, когда панель ОБЪЯСНЯЕТ ту область, над которой встаёт: словарь
   * маркеров колонки, раскрытый прямо над этими маркерами, заставляет читать
   * ключ и терять то, что он расшифровывает. Сдвиг уводит панель за правый
   * край своей колонки. Не `align`: тот выравнивает по краям триггера, а
   * триггер здесь — иконка 24px, от её краёв сдвинуть некуда.
   */
  offsetX?: number;
  className?: string;
  /** Класс на span-якорь триггера (`.root`) — напр. width:100% в ячейке таблицы,
   *  чтобы триггер заполнял ячейку и шеврон прижимался к правому краю. */
  triggerClassName?: string;
};

/** Клавиши, которые содержимое панели считает своими: наружу они не уходят. */
const NAV_KEYS = new Set([
  'Enter',
  ' ',
  'ArrowDown',
  'ArrowUp',
  'ArrowLeft',
  'ArrowRight',
  'Home',
  'End',
]);

/** Отступ от краёв viewport при clamp/flip (px). */
const GUTTER = 8;
/** Вертикальный зазор между триггером и панелью (px). */
const GAP = 4;
/** Запасная высота панели до измерения реального DOM (px). */
const FALLBACK_PANEL_HEIGHT = 320;

type PanelPosition = {
  left: number;
  top: number;
  /** Раскрытие вверх (flip) — для transform-origin анимации. */
  flipped: boolean;
  /**
   * Сколько места есть у панели по вертикали в лучшую из сторон (px).
   *
   * Отдаётся содержимому переменной `--tm-popover-avail`: панели со СПИСКОМ
   * упирались в константу-потолок и показывали пять строк из десяти, когда под
   * якорем оставалось ещё триста пикселей пустоты (гейт F26, раунд 7). Считается
   * от прямоугольника ТРИГГЕРА и окна — от высоты самой панели не зависит, и
   * обратной связи «высота → потолок → высота» не возникает.
   */
  avail: number;
};

/**
 * TaskPopover — anchored-попап для inline-редакторов задач (FE-S12.2-12..17, FE-1).
 *
 * Рендерится в `document.body` через `createPortal` с `position:fixed` от
 * `trigger.getBoundingClientRect()` — это выводит панель из всех overflow/clip
 * контекстов (drawer `.main`, kanban `.stack`), покрывая F-UI-10/F-UX-12.
 *
 * Collision-aware: измеряет rect триггера + размер панели при открытии
 * (`useLayoutEffect`), при необходимости меняет align на end и clamp'ит left в
 * пределах viewport; при нехватке места снизу — flip вверх. Пересчёт на resize
 * и scroll (window + ближайший scroll-контейнер).
 *
 * Закрытие: клик вне, Esc. Не использует native select (feedback_ui_native_selects).
 */
export function TaskPopover({
  trigger,
  children,
  haspopup = 'dialog',
  panelLabel,
  align = 'start',
  width = 280,
  density = 'menu',
  maxHeight,
  offsetX = 0,
  className = '',
  triggerClassName = '',
}: TaskPopoverProps): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<PanelPosition | null>(null);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const panelId = useId();

  const close = useCallback(() => setIsOpen(false), []);
  const open = useCallback(() => setIsOpen(true), []);

  /** Вычислить позицию панели от rect триггера с учётом границ окна. */
  const computePosition = useCallback((): void => {
    const triggerEl = triggerRef.current;
    if (!triggerEl) return;
    const rect = triggerEl.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    const panelW = width;
    const panelH = panelRef.current?.offsetHeight ?? FALLBACK_PANEL_HEIGHT;

    // --- Горизонталь: align как предпочтение, затем clamp в viewport. ---
    // start → левый край панели у левого края триггера; end → правый край у правого края триггера.
    // `offsetX` применяется ДО clamp'а: это предпочтение, а не гарантия — у
    // правого края окна clamp всё равно вернёт панель внутрь.
    let left = (align === 'end' ? rect.right - panelW : rect.left) + offsetX;

    // Если предпочтение start вылезает за правый край — попробовать выровнять по end.
    if (align === 'start' && left + panelW > vw - GUTTER) {
      left = rect.right - panelW;
    }
    // Жёсткий clamp в [GUTTER, vw - panelW - GUTTER].
    const maxLeft = vw - panelW - GUTTER;
    if (maxLeft >= GUTTER) {
      left = Math.min(Math.max(left, GUTTER), maxLeft);
    } else {
      // Панель шире доступной ширины — прижать к левому краю.
      left = GUTTER;
    }

    // --- Вертикаль: под триггером, иначе flip вверх. ---
    let top = rect.bottom + GAP;
    let flipped = false;
    if (top + panelH > vh - GUTTER) {
      const aboveTop = rect.top - GAP - panelH;
      // Flip вверх только если так помещается лучше (или хотя бы помещается).
      if (aboveTop >= GUTTER) {
        top = aboveTop;
        flipped = true;
      } else {
        // Ни снизу, ни сверху не помещается целиком — clamp снизу с GUTTER.
        top = Math.max(GUTTER, vh - panelH - GUTTER);
      }
    }

    const avail = Math.max(vh - rect.bottom - GAP - GUTTER, rect.top - GAP - GUTTER);
    setPosition({ left, top, flipped, avail });
  }, [align, width, offsetX]);

  // Измерение позиции при открытии (до paint) + после монтирования панели.
  useLayoutEffect(() => {
    if (!isOpen) {
      setPosition(null);
      return;
    }
    computePosition();
  }, [isOpen, computePosition]);

  // Закрытие: клик вне (триггер + панель), Esc.
  useEffect(() => {
    if (!isOpen) return;
    function onPointerDown(e: PointerEvent) {
      const target = e.target as Node;
      const inTrigger = triggerRef.current?.contains(target);
      const inPanel = panelRef.current?.contains(target);
      // Клик внутри ЛЮБОЙ другой popover-панели (вложенный поповер портал —
      // operator-select / multiselect / add-field в FilterCard) НЕ закрывает
      // этот поповер: вложенные панели порталятся в body, вне panelRef.
      const el = target instanceof Element ? target : (target as Node).parentElement;
      const inAnyPopover = el?.closest('[data-popover-panel]') != null;
      if (!inTrigger && !inPanel && !inAnyPopover) close();
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        close();
      }
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [isOpen, close]);

  // Пересчёт на resize + scroll (window capture ловит и ближайший scroll-контейнер).
  useEffect(() => {
    if (!isOpen) return;
    const onReflow = () => computePosition();
    window.addEventListener('resize', onReflow);
    // capture:true — scroll не всплывает, но в фазе capture доходит до window
    // от любого вложенного scroll-контейнера (drawer .main, kanban .stack).
    window.addEventListener('scroll', onReflow, true);
    return () => {
      window.removeEventListener('resize', onReflow);
      window.removeEventListener('scroll', onReflow, true);
    };
  }, [isOpen, computePosition]);

  // F22 — фокус входит в панель ТОЛЬКО после измерения позиции.
  //
  // Первый кадр панель рендерится с `visibility:hidden` (чтобы не мелькнуть в
  // (0,0)), а `focus()` на скрытом элементе браузер игнорирует молча. Прежняя
  // редакция фокусировала список в пассивном `useEffect` его собственного
  // монтирования — то есть ровно в тот кадр, и фокус оставался на триггере:
  // клавиатурный путь смены статуса не существовал вовсе.
  const ready = position !== null;
  useEffect(() => {
    if (!isOpen || !ready) return;
    const panel = panelRef.current;
    if (!panel) return;
    // `[data-autofocus]` — явное пожелание содержимого (поле ввода, активная опция).
    const target =
      panel.querySelector<HTMLElement>('[data-autofocus]') ?? firstFocusable(panel) ?? panel;
    target.focus();
  }, [isOpen, ready]);

  // Возврат фокуса на триггер при закрытии — иначе после выбора фокус остаётся
  // на удалённом узле, браузер роняет его в <body>, и следующий Tab начинает
  // обход страницы заново (в плотной таблице это потеря места).
  const wasOpen = useRef(false);
  useEffect(() => {
    if (isOpen) {
      wasOpen.current = true;
      return;
    }
    if (!wasOpen.current) return;
    wasOpen.current = false;
    const active = document.activeElement;
    // Не отбирать фокус, если пользователь уже увёл его сам (клик по другой ячейке).
    if (active && active !== document.body && !triggerRef.current?.contains(active)) return;
    firstFocusable(triggerRef.current)?.focus();
  }, [isOpen]);

  /**
   * Панель — портал в `document.body`, но в дереве React она остаётся ребёнком
   * своей ячейки: события всплывают к строке таблицы. Из-за этого Enter по опции
   * статуса доходил до грида и открывал карточку задачи «заодно», а клик по
   * опции считался кликом по строке. Гасим оба класса на корне панели.
   */
  const onPanelClick = useCallback((e: React.MouseEvent): void => {
    e.stopPropagation();
  }, []);

  /** Tab внутри панели зациклен: панель порталится в конец body, и без цикла
   *  Tab уводит фокус в служебные элементы страницы мимо всего содержимого. */
  const onPanelKeyDown = useCallback((e: ReactKeyboardEvent<HTMLDivElement>): void => {
    if (NAV_KEYS.has(e.key)) e.stopPropagation();
    /**
     * WTU-F3 — стрелки ВОДЯТ по пунктам, а не только гасятся.
     *
     * Найдено `ux-design-reviewer` на раунде 2 гейта фазы A: панель объявляет
     * себя меню (`role="menu"` у содержимого) и забирает `ArrowUp`/`ArrowDown`
     * себе, но фокус ими не двигает — ходить приходилось Tab-ом, хотя для меню
     * стрелки и есть ожидаемый жест (STAITER_UX §8). Хуже нейтрального: клавиша
     * перехвачена и не делает ничего, то есть меню выглядит сломанным.
     *
     * Текстовый ввод исключён явно: в поле стрелки двигают КАРЕТКУ, и отбирать
     * их у него значило бы менять одну поломку на другую. По той же причине
     * пропускается содержимое, объявившее себя сеткой (календарь двигает
     * фокус по датам сам).
     */
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Home' || e.key === 'End') {
      const panel = panelRef.current;
      if (!panel) return;
      const active = document.activeElement;
      if (active instanceof HTMLElement && isTextEntry(active)) return;
      const items = focusables(panel);
      if (items.length === 0) return;
      const idx = active instanceof HTMLElement ? items.indexOf(active) : -1;
      let next: HTMLElement | undefined;
      if (e.key === 'Home') next = items[0];
      else if (e.key === 'End') next = items[items.length - 1];
      else if (e.key === 'ArrowDown') next = items[idx < 0 ? 0 : (idx + 1) % items.length];
      else next = items[idx <= 0 ? items.length - 1 : idx - 1];
      if (!next) return;
      e.preventDefault();
      next.focus();
      return;
    }
    if (e.key !== 'Tab') return;
    const panel = panelRef.current;
    if (!panel) return;
    const items = focusables(panel);
    if (items.length === 0) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (!first || !last) return;
    const active = document.activeElement;
    if (e.shiftKey && (active === first || active === panel)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  }, []);

  const triggerProps: TaskPopoverTriggerProps = {
    'aria-haspopup': haspopup,
    'aria-expanded': isOpen,
    'aria-controls': isOpen ? panelId : undefined,
    onKeyDown: (e) => {
      // ArrowDown/ArrowUp на закрытом триггере открывают панель — привычный жест
      // комбобокса; Enter/Space уже отрабатывает сам `<button>`.
      if (!isOpen && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
        e.preventDefault();
        open();
      }
    },
  };

  return (
    <span ref={triggerRef} className={[styles.root, triggerClassName].filter(Boolean).join(' ')}>
      {trigger(open, isOpen, triggerProps)}
      {isOpen &&
        createPortal(
          <div
            ref={panelRef}
            id={panelId}
            tabIndex={-1}
            className={[styles.panel, position?.flipped ? styles.flipped : '', className]
              .filter(Boolean)
              .join(' ')}
            style={{
              width,
              ...(maxHeight ? { maxHeight } : {}),
              ...(position ? { ['--tm-popover-avail' as string]: `${position.avail}px` } : {}),
              left: position?.left ?? 0,
              top: position?.top ?? 0,
              // До первого измерения панель невидима, чтобы не мелькнула в (0,0).
              visibility: position ? 'visible' : 'hidden',
            }}
            role="dialog"
            {...(panelLabel ? { 'aria-label': panelLabel } : {})}
            onKeyDown={onPanelKeyDown}
            onClick={onPanelClick}
            data-popover-panel=""
            data-density={density}
          >
            {children(close, ready)}
          </div>,
          document.body,
        )}
    </span>
  );
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Элемент, для которого стрелки означают движение каретки/значения, а не
 * переход между пунктами: текстовые поля, редактируемая область, содержимое
 * внутри сетки (календарь) и любой контрол, объявивший собственную навигацию.
 */
function isTextEntry(el: HTMLElement): boolean {
  if (el.isContentEditable) return true;
  const tag = el.tagName;
  if (tag === 'TEXTAREA') return true;
  if (tag === 'INPUT') {
    const type = (el as HTMLInputElement).type;
    return type !== 'checkbox' && type !== 'radio' && type !== 'button';
  }
  return el.closest('[role="grid"], [data-arrow-nav="self"]') !== null;
}

function focusables(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => el.offsetParent !== null || el === document.activeElement,
  );
}

function firstFocusable(root: HTMLElement | null): HTMLElement | null {
  return root ? (focusables(root)[0] ?? null) : null;
}
